import { useEffect } from 'react';

type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;

const SITE_ORIGIN = 'https://mendllabs.ai';

export interface UseSeoOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  noindex?: boolean;
  jsonLd?: JsonLd;
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

export function useSeo({
  title,
  description,
  canonicalPath,
  noindex = false,
  jsonLd,
}: UseSeoOptions) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Mendl Labs' });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalPath ? new URL(canonicalPath, SITE_ORIGIN).toString() : window.location.href,
    });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    if (canonicalPath) {
      upsertLink('link[rel="canonical"]', {
        rel: 'canonical',
        href: new URL(canonicalPath, SITE_ORIGIN).toString(),
      });
    }

    const existingJsonLd = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="true"]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      document.head.querySelector('script[data-seo-jsonld="true"]')?.remove();
    };
  }, [canonicalPath, description, jsonLd, noindex, title]);
}